import type React from 'react';
import {Box, Button, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography} from '@mui/material';
import {Tag} from '~/components/tag/Tag';
import {pct, share, TIER_COLOR, unitPrice} from '../../domain/shouldCost';
import type {PriceModel} from '../../types/shouldCostTypes';
import {ProfileSection} from '../components/ProfileSection';
import {RowAction} from '../components/RowAction';
import {SHOULD_COST_HELP} from '../../domain/shouldCostHelp';
import {LOCALE} from '~/pages/category-profile/domain/locale';

const MONTH = new Intl.DateTimeFormat(LOCALE, {month: 'short', year: 'numeric'});

type Props = {
    model: PriceModel;
    canEdit: boolean;
    onEdit: () => void;
    onActivate: () => void;
    onRemove: () => void;
};

/**
 * Price model (spec 4.9) with its cost driver profile: each cost line, its driver (tier dot, change since the base
 * month, latest month), the base cost and the cost today. "Calculate from index" is automatic: costs follow the
 * latest index values on every view.
 */
export const PriceModelCard: React.FC<Props> = ({model, canEdit, onEdit, onActivate, onRemove}) => {
    const indexed = model.lines.filter(l => l.series);
    const mappedPct = share(indexed.reduce((t, l) => t + l.cost, 0), model.shouldCost);
    return (
        <ProfileSection title="Price model" help={SHOULD_COST_HELP.priceModel} subtitle={`${indexed.length} active driver${indexed.length === 1 ? '' : 's'} · ${mappedPct.toFixed(0)}% of cost mapped`}
                        actions={canEdit && (
                            <>
                                {!model.active && <Button size="small" onClick={onActivate}>Set as active</Button>}
                                <Button size="small" variant="outlined" onClick={onEdit}>Edit model</Button>
                                <RowAction kind="delete" label="price model" onClick={onRemove}/>
                            </>
                        )}>
            <Stack gap={1.5}>
                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="subtitle1" sx={{fontWeight: 600}}>{model.name}</Typography>
                    {model.active && <Tag label="Active" color="success"/>}
                    <Typography variant="caption" color="text.secondary">
                        {`Per ${model.unit} · ${model.currency} · base ${MONTH.format(new Date(model.baseMonth))}`
                            + `${model.annualVolume ? ` · ${model.annualVolume.toLocaleString(LOCALE)} ${model.unit} a year` : ''}`}
                    </Typography>
                </Stack>
                <Box sx={{overflowX: 'auto'}}>
                    <Table size="small" aria-label="Price model cost lines">
                        <TableHead>
                            <TableRow>
                                <TableCell>Line item</TableCell>
                                <TableCell>Cost driver</TableCell>
                                <TableCell align="right">Since base</TableCell>
                                <TableCell align="right">Base cost</TableCell>
                                <TableCell align="right">Should cost</TableCell>
                                <TableCell sx={{width: 140}}>Share</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {model.lines.map(l => (
                                <TableRow key={l.id}>
                                    <TableCell>{l.name}</TableCell>
                                    <TableCell>
                                        <Stack direction="row" gap={1} alignItems="center">
                                            <Tooltip title={`${l.tier[0].toUpperCase()}${l.tier.slice(1)} data`}>
                                                <Box sx={{width: 8, height: 8, borderRadius: '50%', bgcolor: TIER_COLOR[l.tier], flexShrink: 0}}/>
                                            </Tooltip>
                                            <Typography variant="body2" color={l.series ? 'text.primary' : 'text.secondary'}>
                                                {l.series?.name ?? 'Fixed (no driver)'}
                                            </Typography>
                                        </Stack>
                                        {l.latestMonth && (
                                            <Typography variant="caption" color="text.secondary">{`Latest ${MONTH.format(new Date(l.latestMonth))}`}</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">{pct(l.indexChangePct)}</TableCell>
                                    <TableCell align="right">{unitPrice(l.baseCost, model.currency)}</TableCell>
                                    <TableCell align="right" sx={{fontWeight: 600}}>{unitPrice(l.cost, model.currency)}</TableCell>
                                    <TableCell>
                                        <Stack direction="row" gap={1} alignItems="center">
                                            <Box sx={{flex: 1, height: 6, bgcolor: 'grey.100', borderRadius: 3}}>
                                                <Box sx={{width: `${share(l.cost, model.shouldCost)}%`, height: '100%', bgcolor: 'primary.main', borderRadius: 3}}/>
                                            </Box>
                                            <Typography variant="caption" sx={{width: 36, textAlign: 'right'}}>{`${share(l.cost, model.shouldCost).toFixed(0)}%`}</Typography>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                            <TableRow>
                                <TableCell colSpan={4} sx={{fontWeight: 600}}>{`Should cost per ${model.unit}`}</TableCell>
                                <TableCell align="right" sx={{fontWeight: 700}}>{unitPrice(model.shouldCost, model.currency)}</TableCell>
                                <TableCell/>
                            </TableRow>
                        </TableBody>
                    </Table>
                </Box>
            </Stack>
        </ProfileSection>
    );
};
